import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { RoleBadge } from "@/components/Shared";
import { Megaphone, Search } from "lucide-react";
import { warn } from "@/lib/logger";

const ROLES = ["P", "D", "C", "A"];

/**
 * The "chiama un giocatore" modal shown when it is my turn to call in classic mode
 * or any turn in secret mode. On confirm, invokes POST /rooms/{id}/call-player.
 */
export default function CallPlayerDialog({ roomId, soldPlayerIds, isSecret }) {
  const [open, setOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState("P");
  const [search, setSearch] = useState("");
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [price, setPrice] = useState(0);

  useEffect(() => {
    api.get(`/players`, { params: { role: roleFilter, search: search || undefined } })
      .then((r) => setPlayers(r.data))
      .catch((err) => warn("call-player players load failed:", err));
  }, [roleFilter, search]);

  const available = useMemo(
    () => players.filter((p) => !soldPlayerIds.includes(p.id)),
    [players, soldPlayerIds],
  );

  const confirm = async () => {
    if (!selected) return;
    try {
      await api.post(`/rooms/${roomId}/call-player`, { player_id: selected.id, base_price: Number(price) });
      toast.success(`Hai chiamato ${selected.name}!`);
      setOpen(false);
      setSelected(null);
      setPrice(0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="call-player-btn" size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black h-12 px-8">
          <Megaphone className="w-5 h-5 mr-2" />Chiama un giocatore
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl bg-slate-950 border-white/10 max-h-[85vh] overflow-y-auto scroll-thin">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black">Chiama un calciatore all'asta</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Tabs value={roleFilter} onValueChange={setRoleFilter}>
            <TabsList className="bg-slate-900/60 border border-white/10">
              {ROLES.map((r) => (
                <TabsTrigger key={r} value={r} data-testid={`filter-${r}`}
                             className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950 font-black">
                  {r}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
            <Input data-testid="player-search" placeholder="Cerca calciatore…"
                   value={search} onChange={(e) => setSearch(e.target.value)}
                   className="pl-9 bg-slate-900/60 border-white/10 h-11" />
          </div>
          <div className="max-h-[45vh] overflow-y-auto scroll-thin space-y-1 pr-1">
            {available.map((p) => (
              <button key={p.id} onClick={() => { setSelected(p); setPrice(0); }}
                      data-testid={`player-row-${p.id}`}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${selected?.id === p.id ? "border-emerald-400 bg-emerald-500/10" : "border-white/5 hover:border-emerald-500/40 bg-slate-900/40"}`}>
                <RoleBadge role={p.role} />
                <div className="flex-1 text-left">
                  <div className="font-bold">{p.name}</div>
                  <div className="text-xs text-slate-400">{p.team}</div>
                </div>
                <div className="font-mono font-bold text-slate-300">{p.quotation}</div>
              </button>
            ))}
          </div>
          {selected && (
            <div className="flex items-center gap-3 pt-3 border-t border-white/10">
              {!isSecret && (
                <>
                  <Input data-testid="call-price-input" type="number" min="0" value={price}
                         onChange={(e) => setPrice(e.target.value)}
                         className="bg-slate-900/60 border-white/10 h-11 w-32 font-mono" />
                  <span className="text-sm text-slate-400">FM prezzo base (0 = da 0)</span>
                </>
              )}
              {isSecret && <span className="text-sm text-slate-400 flex-1">In modalità segreta l'asta inizierà con offerte cieche</span>}
              <div className="flex-1" />
              <Button data-testid="call-confirm" onClick={confirm}
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-11">
                Chiama {selected.name}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
